import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as statesApi from './states.api';

import type { CreateStateRequest, StateListQuery, UpdateStateRequest } from '@utazzo/shared';

const QK = {
  list: (query: StateListQuery) => ['states', 'list', query] as const,
  detail: (id: string) => ['states', 'detail', id] as const,
  countryOptions: ['states', 'country-options'] as const,
};

export const useStatesList = (query: StateListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => statesApi.listStates(query),
    staleTime: 30_000,
  });

export const useCountryOptions = () =>
  useQuery({
    queryKey: QK.countryOptions,
    queryFn: statesApi.listCountryOptions,
    staleTime: 5 * 60_000,
  });

export const useCreateState = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateStateRequest) => statesApi.createState(payload),
    onSuccess: (s) => {
      qc.invalidateQueries({ queryKey: ['states', 'list'] });
      toast.success(`Added ${s.name}`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add state'),
  });
};

export const useUpdateState = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateStateRequest }) =>
      statesApi.updateState(id, payload),
    onSuccess: (s) => {
      qc.invalidateQueries({ queryKey: ['states', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(s.id) });
      toast.success('State updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update state'),
  });
};

export const useDeleteState = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => statesApi.deleteState(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['states', 'list'] });
      toast.success('State deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete state'),
  });
};

export const useBulkDeleteStates = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => statesApi.bulkDeleteStates(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['states', 'list'] }),
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete states'),
  });
};

export const useBulkSetStatesStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, isActive }: { ids: string[]; isActive: boolean }) =>
      statesApi.bulkSetStatesStatus(ids, isActive),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['states', 'list'] }),
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update state status'),
  });
};

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};
